// SentimentDistribution: stacked bar chart showing positive/neutral/negative breakdown.

import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from 'recharts';
import { t } from '../lib/i18n';
import type { Lang } from '../lib/i18n';
import type { SentimentDistribution as SentDist } from '../lib/api';

interface SentimentDistributionProps {
  data: SentDist | null;
  lang: Lang;
}

export function SentimentDistribution({ data, lang }: SentimentDistributionProps) {
  if (!data || data.total === 0) {
    return (
      <div className="card p-6 flex items-center justify-center text-neutral-500 text-sm">
        {t('noData', lang)}
      </div>
    );
  }

  const chartData = [
    {
      name: t('sentimentDistribution', lang),
      [t('positive', lang)]: Math.round(data.positive),
      [t('neutral', lang)]: Math.round(data.neutral),
      [t('negative', lang)]: Math.round(data.negative),
    },
  ];

  return (
    <div className="card p-4">
      <div className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3">
        {t('sentimentDistribution', lang)}
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="text-center">
          <div className="text-lg font-semibold text-emerald-600 tabular-nums">
            {data.total > 0 ? Math.round((data.positive / data.total) * 100) : 0}%
          </div>
          <div className="text-xs text-neutral-500">{t('positive', lang)}</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-semibold text-neutral-600 tabular-nums">
            {data.total > 0 ? Math.round((data.neutral / data.total) * 100) : 0}%
          </div>
          <div className="text-xs text-neutral-500">{t('neutral', lang)}</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-semibold text-rose-600 tabular-nums">
            {data.total > 0 ? Math.round((data.negative / data.total) * 100) : 0}%
          </div>
          <div className="text-xs text-neutral-500">{t('negative', lang)}</div>
        </div>
      </div>

      {/* Stacked bar */}
      <div className="h-2 rounded-full overflow-hidden bg-neutral-200 flex">
        <div
          className="h-full bg-emerald-500"
          style={{ width: `${(data.positive / data.total) * 100}%` }}
        />
        <div
          className="h-full bg-neutral-500"
          style={{ width: `${(data.neutral / data.total) * 100}%` }}
        />
        <div
          className="h-full bg-rose-500"
          style={{ width: `${(data.negative / data.total) * 100}%` }}
        />
      </div>
      <div className="text-xs text-neutral-500 text-right mt-1">
        {t('totalMentions', lang)}: {data.total}
      </div>
    </div>
  );
}
